// 新手导览统一管理（WTK-719）。
//
// - 每套导览各管各的：侧边栏导览（global）首次登录时先播；页面导览第一次打开那一页时才播。
// - 同一时间只播一个；这次打开页面里跳过过一个导览，就不再自动弹别的，下次打开对应页面再播。
// - 每套导览带版本号：改动较大时把 version 加 1，看过旧版本的用户只会再看这一套。
// - 「看过」按登录账号记在 localStorage，换账号不串。
// - Help Center 的「Replay guide」列出全部导览，点哪个只重放哪个。
//
// 加一套新的页面导览：在 TOURS 里加一条（id / version / route / mode / labelKey），页面上给要指的
// 元素加 data-guide-key，再照 views/hotel/components/HomeGuide.vue 写一个包装 components/common/PageGuide.vue 的组件。
import Vue from "vue";
import store from "../store";

// mode：在哪个模块下有这套导览（hotel / restaurant），Help Center 只列当前模块的
export const TOURS = [
  { id: "sidebar", version: 1, global: true, mode: "hotel", labelKey: "guideTours.sidebar" },
  { id: "home", version: 1, route: "/index", mode: "hotel", labelKey: "guideTours.home" },
  { id: "reviews", version: 1, route: "/reviews", mode: "hotel", labelKey: "guideTours.reviews" },
  { id: "restaurantSidebar", version: 1, global: true, mode: "restaurant", labelKey: "guideTours.restaurantSidebar" },
  { id: "restaurantHome", version: 1, route: "/RestaurantDashboard", mode: "restaurant", labelKey: "guideTours.restaurantHome" },
  { id: "restaurantReviews", version: 1, route: "/RestaurantReviewsIndex", mode: "restaurant", labelKey: "guideTours.restaurantReviews" },
];

// 改造前每套导览一个全局 key，值为 "1"；读到时当作看过第 1 版
const LEGACY_KEYS = { sidebar: "sidebarGuideCompleted", home: "homeGuideCompleted" };
const STORAGE_PREFIX = "wtkGuideTours:";

const state = Vue.observable({
  // 正在播放的导览 id
  active: null,
  // 这次打开页面里跳过过导览：不再自动弹别的
  skippedThisVisit: false,
  // 全局导览（侧边栏）这次已经有结论：播完 / 跳过 / 不需要播。页面导览要等它
  globalSettled: false,
  // Help Center 点了重看、要先跳到所在页面的导览 id
  pendingReplay: null,
});
const emitter = new Vue();

function findTour(id) {
  return TOURS.find((t) => t.id === id) || null;
}

// token 是 JWT，sub 是登录邮箱；同步可读，不用等 /auth/me
function userKey() {
  try {
    const token = store.state.auth.token || localStorage.getItem("token") || sessionStorage.getItem("token");
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload && payload.sub) return STORAGE_PREFIX + payload.sub;
  } catch (e) { /* 没有 token 或解析失败 */ }
  return STORAGE_PREFIX + "anon";
}

function readSeen() {
  let seen = {};
  try {
    seen = JSON.parse(localStorage.getItem(userKey()) || "{}") || {};
    Object.keys(LEGACY_KEYS).forEach((id) => {
      if (seen[id] == null && localStorage.getItem(LEGACY_KEYS[id]) === "1") seen[id] = 1;
    });
  } catch (e) { /* storage 不可用 */ }
  return seen;
}

export function isSeen(id) {
  const tour = findTour(id);
  if (!tour) return true;
  try {
    localStorage.getItem("x");
  } catch (e) {
    return true; // storage 不可用时不强制播放
  }
  return (readSeen()[id] || 0) >= tour.version;
}

function markSeen(id) {
  const tour = findTour(id);
  if (!tour) return;
  try {
    const seen = readSeen();
    seen[id] = tour.version;
    localStorage.setItem(userKey(), JSON.stringify(seen));
  } catch (e) { /* ignore */ }
}

// 自动播放（非重看）前的检查：没看过、没有别的导览在播、这次没跳过过
export function shouldAutoStart(id) {
  return !isSeen(id) && !state.active && !state.skippedThisVisit;
}

// 真正开始播放时调用；已有别的导览在播就返回 false
export function begin(id) {
  if (state.active && state.active !== id) return false;
  state.active = id;
  return true;
}

// 播完或跳过（Skip / ×）都算看过；跳过的话这次不再自动弹别的导览
export function end(id, { skipped = false } = {}) {
  if (state.active === id) state.active = null;
  markSeen(id);
  if (skipped) state.skippedThisVisit = true;
}

// 侧边栏导览有结论时调用（播完、跳过、看过、不满足条件、找不到目标都算）
export function settleGlobal() {
  if (state.globalSettled) return;
  state.globalSettled = true;
  emitter.$emit("global-settled");
}

// 页面导览用：全局导览有结论后再回调（已经有结论就立即回调）。返回取消订阅的函数
export function whenGlobalSettled(cb) {
  if (state.globalSettled) {
    cb();
    return () => {};
  }
  emitter.$once("global-settled", cb);
  return () => emitter.$off("global-settled", cb);
}

// Help Center 重看某一套：页面导览不在当前页就先跳过去，由页面组件挂载时 takePendingReplay 接着播
export function requestReplay(id, router) {
  const tour = findTour(id);
  if (!tour) return;
  state.skippedThisVisit = false;
  if (tour.route && router && router.currentRoute.path !== tour.route) {
    state.pendingReplay = id;
    router.push(tour.route).catch(() => {
      if (state.pendingReplay === id) state.pendingReplay = null;
    });
    return;
  }
  emitter.$emit("replay", id);
}

export function takePendingReplay(id) {
  if (state.pendingReplay !== id) return false;
  state.pendingReplay = null;
  return true;
}

export function onReplay(cb) {
  emitter.$on("replay", cb);
  return () => emitter.$off("replay", cb);
}
